import { useCallback } from "react";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";

import { ModalPortal, Text } from "@livechat/design-system-react-components";

import ConfirmActionModal from "components/modals/ConfirmActionModal";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { getRouteByPathname } from "helpers/tickets";
import { getSiloByRoute } from "interfaces/entities/ticket";
import { TicketsViewActions } from "store/views/tickets/actions";
import { getSelectedTicketIds } from "store/views/tickets/selectors";
import { StackingContextLevel } from "styles/stacking-context-level";
import { plural } from "utils/string";

const BulkDeleteModal: React.FC<{ onClose(): void }> = ({ onClose }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const selectedIds = useSelector(getSelectedTicketIds);
  const handleConfirm = useCallback(() => {
    dispatch(TicketsViewActions.performBulkDelete(selectedIds));
    onClose();
    const route = getRouteByPathname(location.pathname);
    const siloFromPath = getSiloByRoute(route);
    trackEngagement("Tickets: Delete forever actions", EventPlace.BulkActions, {
      from: siloFromPath,
      bulkChecked: selectedIds.length,
      place: EventPlace.BulkActions,
    });
  }, [dispatch, onClose, selectedIds, location.pathname]);

  return (
    <ModalPortal zIndex={StackingContextLevel.Modal}>
      <ConfirmActionModal
        onClose={onClose}
        heading="Delete tickets?"
        primaryAction={{
          label: "Delete",
          dataTestId: "delete-modal-button",
          onClick: handleConfirm,
          actionType: "destructive",
        }}
        secondaryAction={{
          label: "Cancel",
          onClick: onClose,
        }}
      >
        <Text>
          You&lsquo;re about to delete {selectedIds.length} {plural(selectedIds.length, "ticket", "tickets")} on this
          page. This operation is irreversible. Are you sure?
        </Text>
      </ConfirmActionModal>
    </ModalPortal>
  );
};

export default BulkDeleteModal;
